import { c as _c } from "react/compiler-runtime";
import { WORLD_MAP_ORBIT_HEADING_SENSITIVITY, WORLD_MAP_ORBIT_MAX_TILT, WORLD_MAP_ORBIT_TILT_SENSITIVITY } from "@components/world-map/world-map-constants";
import { useEffect } from "react";

/** Smallest camera tilt (degrees) allowed while orbiting. */
const MIN_TILT = 0;

/**
 * Enables Google-Maps-style orbiting in the 3D scene: hold Ctrl (or Cmd on Mac)
 * and drag to rotate the camera around the current center and change its tilt.
 * Without the modifier key, dragging still pans as usual. No-op for the 2D view.
 *
 * @param viewRef - Ref to the ArcGIS view (only the 3D SceneView is handled).
 * @param isViewReady - Whether the view has finished initializing.
 */
export function useMapOrbit(viewRef, isViewReady) {
  const $ = _c(4);
  let t0;
  let t1;
  if ($[0] !== isViewReady || $[1] !== viewRef) {
    t0 = () => {
      const view = viewRef.current;
      if (!isViewReady || !view || view.type !== "3d") {
        return;
      }
      let lastX = 0;
      let lastY = 0;
      let targetHeading = 0;
      let targetTilt = 0;
      let isGoToInFlight = false;
      let hasPendingOrbit = false;
      function applyOrbit() {
        const center = view?.center;
        if (!view || !center) {
          return;
        }
        isGoToInFlight = true;
        hasPendingOrbit = false;
        view.goTo({
          center,
          heading: targetHeading,
          tilt: targetTilt
        }, {
          animate: false
        }).catch(_temp).finally(() => {
          isGoToInFlight = false;
          if (hasPendingOrbit) {
            applyOrbit();
          }
        });
      }
      const handle = view.on("drag", event => {
        if (!event.native.ctrlKey && !event.native.metaKey) {
          return;
        }
        event.stopPropagation();
        if (event.action === "start") {
          lastX = event.x;
          lastY = event.y;
          targetHeading = view.camera.heading;
          targetTilt = view.camera.tilt;
          return;
        }
        if (event.action !== "update") {
          return;
        }
        const deltaX = event.x - lastX;
        const deltaY = event.y - lastY;
        lastX = event.x;
        lastY = event.y;
        targetHeading = targetHeading + deltaX * WORLD_MAP_ORBIT_HEADING_SENSITIVITY;
        targetHeading;
        targetTilt = Math.min(Math.max(targetTilt - deltaY * WORLD_MAP_ORBIT_TILT_SENSITIVITY, MIN_TILT), WORLD_MAP_ORBIT_MAX_TILT);
        if (isGoToInFlight) {
          hasPendingOrbit = true;
        } else {
          applyOrbit();
        }
      });
      return () => {
        handle.remove();
      };
    };
    t1 = [viewRef, isViewReady];
    $[0] = isViewReady;
    $[1] = viewRef;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
}
function _temp() {}